import { Stack } from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '/@/components/spinner';
import { useCatalogProductById } from '/@/hooks/catalog';
import { getClimatePatternChoices, useSoilMoistures } from '/@/hooks/recommendation';
import { getCropConfig, getValidPlantingDatesFromZones } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import ConditionsFields from '/@/views/recommendations/v2/steps/environment/conditions-fields';

/**
 * Countries that resolve soil moistures from environment points, which need only the country
 * and a planting date. The rest resolve them from coordinates — which this step doesn't have,
 * since no field has been chosen yet — so they skip the question and pick it up at step 3.
 */
const ENVIRONMENT_POINT_COUNTRIES = ['AR', 'BR', 'MX', 'PY'];

/**
 * "Condiciones ambientales" — the product-first wizard's opening step.
 *
 * The hybrid wizard asks these questions after a field is chosen, and sources their options
 * from that field. Here they come first, because the field list that follows is ranked by how
 * the hybrid yields under exactly these conditions — the endpoint behind it takes the planting
 * date and climate year as required parameters. With no field yet, the planting window comes
 * from the product's own zones and the soil moistures from the country.
 */
function ConditionsStep({ productId, crop = 'corn' }: { productId?: number; crop?: 'corn' | 'soy' }) {
    const dispatch = useDispatch();
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);
    const country = useSelector(getSessionCountry);

    const { data: product } = useCatalogProductById({ id: productId, crop }, { enabled: !!productId });

    const usesEnvironmentPoints = ENVIRONMENT_POINT_COUNTRIES.includes(country?.code);
    const soilMoisturesQuery = useSoilMoistures(
        {
            // No field yet, so there are no coordinates to resolve from — these countries
            // resolve from environment points keyed on the country alone.
            lat: undefined,
            lng: undefined,
            countryCode: country?.code,
            climatePattern: cropConfig?.climatePattern,
            plantingDate: cropConfig?.plantingDate,
        },
        { enabled: usesEnvironmentPoints && !!cropConfig?.plantingDate && !!cropConfig?.climatePattern }
    );

    // Nothing on this step can be drawn until the product's planting windows and the moisture
    // options are in — `useSoilMoistures` keys on null while disabled, so this settles for
    // countries that resolve moisture from coordinates rather than hanging on them.
    const isInitialLoading = (!!productId && !product) || soilMoisturesQuery.isLoading;

    // The product's zones stand in for the field's, which is what bounds the calendar.
    const validPlantingDates = useMemo(
        () => getValidPlantingDatesFromZones(product?.zones, crop),
        [product?.zones, crop]
    );

    const soilMoistures = soilMoisturesQuery.data;
    // Default the moisture as soon as the options land, the same way `useRecommendationEditData`
    // does for the field-first wizard — the middle option, so the two flows open on the same
    // reading. Without this the step renders every chip unselected and the user has to guess
    // that a pick is expected. A value the options still contain is left alone, so this never
    // overrides what the user chose; an empty option list means the country has no moisture
    // reading at all, which is `null` rather than a pick.
    useEffect(() => {
        if (!soilMoistures) return;
        if (!soilMoistures.length) {
            dispatch(updateActiveRecommendationCropConfig({ key: 'soilMoisture', value: null }));
            return;
        }
        if (soilMoistures.some((option) => option.value === cropConfig?.soilMoisture)) return;
        const middle = soilMoistures[Math.min(Math.floor(soilMoistures.length / 2), soilMoistures.length - 1)];
        dispatch(updateActiveRecommendationCropConfig({ key: 'soilMoisture', value: middle.value }));
    }, [soilMoistures, cropConfig?.soilMoisture, dispatch]);

    const handleChange = (key: string) => {
        return async (value: unknown) => {
            await dispatch(updateActiveRecommendationCropConfig({ key, value }));
        };
    };

    if (!cropConfig) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    return (
        <Stack spacing={4} pb={4}>
            <ConditionsFields
                plantingDate={cropConfig.plantingDate}
                soilMoisture={cropConfig.soilMoisture}
                climatePattern={cropConfig.climatePattern}
                soilMoistureChoices={soilMoistures || []}
                climatePatternChoices={getClimatePatternChoices()}
                validPlantingDates={validPlantingDates}
                isInitialLoading={isInitialLoading}
                onChange={handleChange}
                crop={crop}
                countryCode={country?.code}
            />
        </Stack>
    );
}

export default ConditionsStep;
